import { TFunction } from 'i18next'
import { useCallback, useEffect } from 'react'
import { toast } from 'react-toastify'
import { invalidateMaintenanceCache } from 'src/api/maintenance'
import { invalidateMaintenanceTemplateCache } from 'src/api/maintenanceTemplate'
import { invalidateCustomer } from '../../api/customer'
import { useGetFile } from '../../api/file'
import { invalidateLocalityCache, useGetLocality, useUpdateLocality } from '../../api/localities'
import { LocalityForm } from '../../forms/LocalityForm'
import { IFile, ILocality } from '../../generated-types'
import { ShowDialogProps } from '../../show-dialog'
import { handleLocalityErrorResponse } from './utils'
import { invalidateMainComponentCache } from 'src/api/main-component'

interface UpdateServiceProps extends ShowDialogProps {
  customerId: EntityId
  locality: ILocality
  t: TFunction
}

export const UpdateLocalityDialog: React.FC<UpdateServiceProps> = ({ pop, customerId, t, locality }) => {
  const { data, refetch } = useGetFile(locality.fileId ? locality.fileId : 0)
  const {
    data: logo,
    refetch: refetchLogo,
    isLoading: isLoadingLogo,
  } = useGetFile(locality.logoId ? locality.logoId : 0)
  const { mutateAsync, isLoading } = useUpdateLocality(customerId as EntityId, locality.id)
  const { data: localityData } = useGetLocality(customerId as EntityId, locality.id, { isIgnoreQueryFilters: true })
  useEffect(() => {
    if (locality.fileId) {
      refetch()
    }
  }, [locality.fileId])

  useEffect(() => {
    if (locality.logoId) {
      refetchLogo()
    }
  }, [locality.logoId])

  const onSubmit = useCallback(
    async form => {
      await mutateAsync(form, {
        onSuccess: () => {
          invalidateLocalityCache.getLocalities(customerId)
          invalidateLocalityCache.getLocality(customerId, locality?.id, undefined)
          invalidateCustomer.getCustomerLocalities()
          invalidateLocalityCache.getLocalitiesWithInfoCount(customerId)
          invalidateLocalityCache.CustomerLocalitiesWithGroups(customerId)
          invalidateMaintenanceTemplateCache.getLocalityMaintenanceTemplates(customerId)
          invalidateLocalityCache.getGroupedLocalitiesWithInfoCount(customerId)
          invalidateMaintenanceCache.getLocalityMaintenances(customerId, locality?.id)
          invalidateLocalityCache.getMaintenanceTypesCount(customerId, locality?.id)
          invalidateCustomer.getCustomerUsersForAdmins(customerId as EntityId, true)
          invalidateMaintenanceCache.getScheduledMaintenanceByMaintenance(customerId, locality?.id)
          invalidateLocalityCache.getCageStatus(customerId, locality?.id)
          invalidateLocalityCache.getLocality(customerId, locality?.id)
          invalidateMainComponentCache.getMainComponentById(customerId)
          toast.success(t('updatedSuccessfully', { ns: 'common' }))
          pop()
        },
        onError: (error: any) => {
          handleLocalityErrorResponse(error.data)
        },
      })
    },
    [customerId, locality?.id],
  )

  return localityData ? (
    <LocalityForm
      isLoading={isLoading}
      onSubmit={onSubmit}
      onCancel={() => pop()}
      initialValues={localityData}
      file={data as IFile}
      logo={logo as IFile}
      customerId={customerId as number}
      title={t('updateLocality', { ns: 'customer-locality' })}
      isLoadingLogo={isLoadingLogo}
    />
  ) : (
    <></>
  )
}
